import {
  ApiErrorResponse,
  IExceptionListCreate,
} from "@/@types/exception-list";
import { ExceptionTypeEnum } from "@/enum/exception-list";
import { useCreateExceptionListMutation } from "@/services/exception-list";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "components/ui/form";
import { toast } from "components/ui/use-toast";
import { ArrowLeft } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useExceptionItemActions } from "./components/tabs/ducks/hooks/action";
import { useExceptionItems } from "./components/tabs/ducks/hooks/state";
import { ExceptionItem } from "./components/tabs/ducks/type";
import { TabsComponent } from "./components/tabs/tabs";
import { useStepActions } from "./duck/hooks/action";
import { MESSAGES_EXCEPTION } from "./utils/message";
import { FormSchema } from "./validation-form";
import { ExceptionFormFields } from "./components/formFields/exception-form-fields";
import { ConfirmRegisterButton } from "./components/confirm-dialog";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";

export const CreateExceptionTaxRoutes: React.FC = () => {
  const items = useExceptionItems();
  const [createExceptionList] = useCreateExceptionListMutation();
  const navigate = useNavigate();

  const form = useForm<IExceptionListCreate>({
    resolver: zodResolver(FormSchema),
    mode: "onSubmit",
    defaultValues: {
      reason: "9",
    },
  });
  const exceptionType = form.watch("exception_type");
  useEffect(() => {
    form.setValue(
      "reason",
      exceptionType === ExceptionTypeEnum.ICMS_DESONERADO ? "9" : undefined,
    );
  }, [exceptionType, form, form.setValue]);

  const { reset: resetForm } = form;
  const { reset: resetItems } = useExceptionItemActions();
  const { reset: resetStep } = useStepActions();

  useEffect(() => {
    resetForm();
    resetItems();
    resetStep();
  }, []);

  const onSubmit = async (formData: IExceptionListCreate) => {
    try {
      const exceptionItems = items.map((item: ExceptionItem) => ({
        ...formData,
        ...item.data,
        type: item.type === "Produto" ? "PRODUCT" : "NCM",
        exception_rate: parseFloat(
          String(formData.exception_rate).replace(",", "."),
        ),
        ncm: item.data.ncm?.replace(/\D/g, ""),
        sku: item.data.sku,
        calculation_basis: parseFloat(
          String(formData.calculation_basis).replace(",", "."),
        ),
        state: formData.state === "TODOS" ? "all" : formData.state,
        initial_term: formData.initial_term,
        final_term: formData.final_term || undefined,
        classification_tax: formData.classification_tax || undefined,
      }));
      await createExceptionList(
        exceptionItems as IExceptionListCreate[],
      ).unwrap();

      navigate("/fiscal/excecao-de-tributos", {
        state: { shouldRefetch: true },
      });
      toast(MESSAGES_EXCEPTION.CREATE.SUCCESS);
      resetForm();
      resetItems();
      resetStep();
    } catch (error) {
      const err = error as { data: ApiErrorResponse };

      toast({
        variant: "destructive",
        description:
          err.data.messages?.[0] || "Erro inesperado ao cadastrar exceção.",
        duration: 5000,
      });

      console.error("Erro do backend:", err.data);
    }
  };
  return (
    <PageLayout>
      <div className="flex justify-start w-full items-center gap-4">
        <ArrowLeft
          onClick={() => navigate(-1)}
          className="cursor-pointer hover:text-gray-600 transition"
        />
        <PageTitle
          title="Criar Exceção de Tributo"
          description="Preencha os campos abaixo para criar uma nova exceção de tributo."
        />
      </div>
      <ContentContainer>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col w-full"
          >
            <ExceptionFormFields form={form} />

            <div className="w-full mt-16 flex flex-col">
              <TabsComponent />
            </div>

            {items.length > 0 && (
              <div className="flex justify-end w-full mt-6">
                <ConfirmRegisterButton
                  onConfirm={form.handleSubmit(onSubmit)}
                  isLoading={form.formState.isSubmitting}
                />
              </div>
            )}
          </form>
        </Form>
      </ContentContainer>
    </PageLayout>
  );
};
